import { FieldError, Resolver } from "react-hook-form";

import {
  FieldDefinition,
  FilterFormData,
} from "src/base-components/FilterPopover/types";

type CreateFilterResolverParams = {
  fields: FieldDefinition[];
  validateValue?: (
    value: string,
    field: FieldDefinition | undefined,
  ) => string | undefined;
};

export const createFilterResolver = ({
  fields,
  validateValue,
}: CreateFilterResolverParams): Resolver<FilterFormData> => {
  return (formValues) => {
    const errors = {} as Record<string, FieldError>;
    const groups = formValues.filter_condition?.conditions ?? [];

    groups.forEach((group, groupIndex) => {
      const conditions = group?.conditions ?? [];

      if (conditions.length === 0) {
        errors[`filter_condition.conditions.${groupIndex}.conditions`] = {
          type: "required",
          message: "At least one condition is required",
        };
        return;
      }

      conditions.forEach((condition, conditionIndex) => {
        const fieldPath = `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}`;

        // Blank rows (no field, no value) are valid no-ops so users can apply
        // a freshly cleared form and serializers can drop them silently.
        if (!condition.field?.trim() && !condition.value?.trim()) {
          return;
        }

        if (!condition.field?.trim()) {
          errors[`${fieldPath}.field`] = {
            type: "required",
            message: "Field is required",
          };
        }

        if (condition.operator !== "is_none") {
          if (!condition.value.trim()) {
            errors[`${fieldPath}.value`] = {
              type: "required",
              message: "Value is required",
            };
          } else if (validateValue) {
            const fieldDef = fields.find((f) => f.key === condition.field);
            const typeError = validateValue(condition.value, fieldDef);
            if (typeError) {
              errors[`${fieldPath}.value`] = {
                type: "validate",
                message: typeError,
              };
            }
          }
        }
      });
    });

    return {
      values: Object.keys(errors).length > 0 ? {} : formValues,
      errors,
    };
  };
};
